import React, { useState, useEffect } from "react";
import axios from "axios";
import "./offering2.css";

const OfferingForm = () => {
  const initialCategories = {
    combinedOffering: { label: "Combined Offering 10%+", amount: "" },
    ocaChurchBudget: { label: "OCA Church Budget", amount: "" },
    churchBuilding: { label: "Church Building", amount: "" },
    evangelism: { label: "Evangelism", amount: "" },
    campmeetingOffering: { label: "Campmeeting Offering", amount: "" },
    campmeetingExpenses: { label: "Campmeeting Expenses", amount: "" },
    thanksgivingSabbathSchool: {
      label: "Thanksgiving Sabbath School",
      amount: "",
    },
  };

  const predefinedDepartments = [
    { value: "AWM", label: "AWM" },
    { value: "AMO", label: "AMO" },
    { value: "Children", label: "Children" },
  ];

  const [formData, setFormData] = useState({
    categories: initialCategories,
    selectedCategories: {},
    selectedDepartments: {},
    departmentAmounts: {},
    selectedOtherDepartments: {},
    otherDepartmentAmounts: {},
    showDepartment: false,
    showOtherSection: false,
    phoneNumber: "",
  });

  const [otherDepartments, setOtherDepartments] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState({
    loading: false,
    error: null,
    success: false,
    message: "",
  });
  const [error, setError] = useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [runningTotal, setRunningTotal] = useState(0);

  useEffect(() => {
    const fetchOtherDepartments = async () => {
      try {
        const response = await axios.get("/mpesa/other-departments");
        setOtherDepartments(
          response.data.map((dept) => ({
            value: dept.name,
            label: dept.name,
          }))
        );
      } catch (err) {
        console.error("Error fetching other departments:", err);
        setError("Failed to load other departments");
      }
    };

    fetchOtherDepartments();
    setRunningTotal(calculateTotal());
  }, [
    formData.categories,
    formData.departmentAmounts,
    formData.otherDepartmentAmounts,
  ]);

  const calculateTotal = () => {
    const categoryTotal = Object.entries(formData.categories).reduce(
      (sum, [key, value]) =>
        formData.selectedCategories[key]
          ? sum + (parseFloat(value.amount) || 0)
          : sum,
      0
    );

    const departmentTotal = Object.entries(formData.departmentAmounts).reduce(
      (sum, [_, amount]) => sum + (parseFloat(amount) || 0),
      0
    );

    const otherDepartmentTotal = Object.entries(
      formData.otherDepartmentAmounts
    ).reduce((sum, [_, amount]) => sum + (parseFloat(amount) || 0), 0);

    return categoryTotal + departmentTotal + otherDepartmentTotal;
  };

  const handlePayment = async () => {
    setPaymentStatus({
      loading: true,
      error: null,
      success: false,
      message: "Initiating payment...",
    });
    try {
      const otherDepts = Object.entries(formData.selectedOtherDepartments)
        .filter(([dept, selected]) => selected)
        .map(([dept]) => {
          const amount = formData.otherDepartmentAmounts[dept];
          const parsedAmount = parseFloat(amount);
          return {
            label: dept,
            amount: isNaN(parsedAmount) ? 0 : parsedAmount,
          };
        });

      const paymentData = {
        phone: formData.phoneNumber,
        categories: Object.entries(formData.categories)
          .filter(([key]) => formData.selectedCategories[key])
          .reduce(
            (acc, [key, value]) => ({
              ...acc,
              [key]: { amount: parseFloat(value.amount) || 0 },
            }),
            {}
          ),
        departments: {
          predefined: Object.entries(formData.selectedDepartments)
            .filter(([dept, selected]) => selected)
            .reduce(
              (acc, [dept]) => ({
                ...acc,
                [dept]: parseFloat(formData.departmentAmounts[dept] || 0),
              }),
              {}
            ),
          other: otherDepts,
        },
        totalAmount: runningTotal,
      };

      console.log(
        "Sending payment request:",
        JSON.stringify(paymentData, null, 2)
      );
      const response = await axios.post("/mpesa/stk2/push", paymentData);
      console.log("Payment response:", response.data);

      setPaymentStatus({
        loading: false,
        success: true,
        message: "Please check your phone to complete the payment",
      });
    } catch (error) {
      console.error("Payment error:", error.response?.data || error);
      setPaymentStatus({
        loading: false,
        error: true,
        message:
          error.response?.data?.error || "Payment failed. Please try again.",
      });
    }
  };

  const handleCategoryToggle = (category) => {
    setFormData((prev) => ({
      ...prev,
      selectedCategories: {
        ...prev.selectedCategories,
        [category]: !prev.selectedCategories[category],
      },
    }));
  };

  const handleDepartmentToggle = (dept) => {
    setFormData((prev) => ({
      ...prev,
      selectedDepartments: {
        ...prev.selectedDepartments,
        [dept]: !prev.selectedDepartments[dept],
      },
      departmentAmounts: !prev.selectedDepartments[dept]
        ? prev.departmentAmounts
        : { ...prev.departmentAmounts, [dept]: "" },
    }));
  };

  const handleOtherDepartmentToggle = (dept) => {
    setFormData((prev) => ({
      ...prev,
      selectedOtherDepartments: {
        ...prev.selectedOtherDepartments,
        [dept]: !prev.selectedOtherDepartments[dept],
      },
      otherDepartmentAmounts: !prev.selectedOtherDepartments[dept]
        ? prev.otherDepartmentAmounts
        : { ...prev.otherDepartmentAmounts, [dept]: "" },
    }));
  };

  const handleAmountChange = (category, value) => {
    const numValue = parseFloat(value);
    if (value === "" || numValue > 0) {
      setFormData((prev) => ({
        ...prev,
        categories: {
          ...prev.categories,
          [category]: { ...prev.categories[category], amount: value },
        },
      }));
    }
  };

  const handleDepartmentAmountChange = (dept, value) => {
    const numValue = parseFloat(value);
    if (value === "" || numValue > 0) {
      setFormData((prev) => ({
        ...prev,
        departmentAmounts: { ...prev.departmentAmounts, [dept]: value },
      }));
    }
  };

  const handleOtherDepartmentAmountChange = (dept, value) => {
    const numValue = parseFloat(value);
    if (value === "" || numValue > 0) {
      setFormData((prev) => ({
        ...prev,
        otherDepartmentAmounts: {
          ...prev.otherDepartmentAmounts,
          [dept]: value,
        },
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let isValid = true;
    setError("");

    // Check phone number
    if (!formData.phoneNumber) {
      isValid = false;
      setError("Please enter a phone number");
      return;
    }

    // Calculate total and check if anything is selected with a valid amount
    const total = calculateTotal();
    if (total <= 0) {
      isValid = false;
      setError(
        "Please select at least one category or department with an amount greater than 0"
      );
      return;
    }

    // Validate selected categories
    Object.keys(formData.selectedCategories).forEach((category) => {
      if (formData.selectedCategories[category]) {
        const amount = parseFloat(formData.categories[category].amount);
        if (!formData.categories[category].amount || amount <= 0) {
          isValid = false;
          setError(
            `Amount for ${formData.categories[category].label} must be greater than 0`
          );
        }
      }
    });

    // Validate departments if shown
    if (formData.showDepartment) {
      const selectedDepts = Object.keys(formData.selectedDepartments).filter(
        (dept) => formData.selectedDepartments[dept]
      );
      const selectedOtherDepts = Object.keys(
        formData.selectedOtherDepartments
      ).filter((dept) => formData.selectedOtherDepartments[dept]);

      if (selectedDepts.length === 0 && selectedOtherDepts.length === 0) {
        isValid = false;
        setError(
          "Please select at least one department when 'Any Other' is checked"
        );
      } else {
        selectedDepts.forEach((dept) => {
          const amount = parseFloat(formData.departmentAmounts[dept]);
          if (!formData.departmentAmounts[dept] || amount <= 0) {
            isValid = false;
            setError(`Amount for ${dept} must be greater than 0`);
          }
        });

        selectedOtherDepts.forEach((dept) => {
          const amount = parseFloat(formData.otherDepartmentAmounts[dept]);
          if (!formData.otherDepartmentAmounts[dept] || amount <= 0) {
            isValid = false;
            setError(`Amount for ${dept} must be greater than 0`);
          }
        });
      }
    }

    if (isValid) {
      setShowSummary(true);
    }
  };

  const getDepartmentsForDisplay = () => {
    const selectedDepts = Object.entries(formData.selectedDepartments)
      .filter(([dept, selected]) => selected)
      .map(([dept]) => dept);
    const selectedOtherDepts = Object.entries(formData.selectedOtherDepartments)
      .filter(([dept, selected]) => selected)
      .map(([dept]) => dept);
    return [...selectedDepts, ...selectedOtherDepts].join(", ");
  };

  return (
    <div className="offering-form">
      {!showSummary ? (
        <form onSubmit={handleSubmit}>
          <h2>Tithes & Offerings into the Storehouse</h2>

          <div className="form-group phone-input">
            <label>Phone Number (M-Pesa):</label>
            <input
              type="tel"
              name="phone" // Add this to help browser identify the field
              autocomplete="tel" // Tell browser this is a phone number
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  phoneNumber: e.target.value,
                }))
              }
              placeholder="e.g., 254712345678"
              required
            />
          </div>

          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(formData.categories).map(([key, { label }]) => (
                <tr key={key}>
                  <td>
                    <input
                      type="checkbox"
                      checked={formData.selectedCategories[key] || false}
                      onChange={() => handleCategoryToggle(key)}
                    />
                    <label>{label}</label>
                  </td>
                  <td>
                    <input
                      type="number"
                      value={formData.categories[key].amount}
                      onChange={(e) => handleAmountChange(key, e.target.value)}
                      disabled={!formData.selectedCategories[key]}
                      min="1"
                    />
                  </td>
                </tr>
              ))}
              <tr>
                <td>
                  <input
                    type="checkbox"
                    checked={formData.showDepartment}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        showDepartment: e.target.checked,
                      }))
                    }
                  />
                  <label>Any Other</label>
                </td>
                <td />
              </tr>
            </tbody>
          </table>

          {formData.showDepartment && (
            <div className="departments-section">
              <table>
                <thead>
                  <tr>
                    <th>Department</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Predefined Departments */}
                  {predefinedDepartments.map(({ value, label }) => (
                    <tr key={value}>
                      <td className="department-item">
                        <input
                          type="checkbox"
                          checked={formData.selectedDepartments[value] || false}
                          onChange={() => handleDepartmentToggle(value)}
                        />
                        <label>{label}</label>
                      </td>
                      <td>
                        <input
                          type="number"
                          value={formData.departmentAmounts[value] || ""}
                          onChange={(e) =>
                            handleDepartmentAmountChange(value, e.target.value)
                          }
                          disabled={!formData.selectedDepartments[value]}
                          min="1"
                        />
                      </td>
                    </tr>
                  ))}
                  {/* "Other" Section Toggle */}
                  <tr>
                    <td className="other-section">
                      <input
                        type="checkbox"
                        checked={formData.showOtherSection}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            showOtherSection: e.target.checked,
                          }))
                        }
                      />
                      <label>Other</label>
                    </td>
                    <td />
                  </tr>
                  {/* Dynamically Fetched "Other" Departments under "Promotional" */}
                  {formData.showOtherSection && (
                    <>
                      <tr>
                        <td colSpan="2" className="promotional-subheading">
                          Promotional
                        </td>
                      </tr>
                      {otherDepartments.map(({ value, label }) => (
                        <tr key={value} className="other-department-row">
                          <td className="other-department-item">
                            <input
                              type="checkbox"
                              checked={
                                formData.selectedOtherDepartments[value] ||
                                false
                              }
                              onChange={() =>
                                handleOtherDepartmentToggle(value)
                              }
                            />
                            <label>{label}</label>
                          </td>
                          <td>
                            <input
                              type="number"
                              value={
                                formData.otherDepartmentAmounts[value] || ""
                              }
                              onChange={(e) =>
                                handleOtherDepartmentAmountChange(
                                  value,
                                  e.target.value
                                )
                              }
                              disabled={
                                !formData.selectedOtherDepartments[value]
                              }
                              min="1"
                            />
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {error && <p className="error-message">{error}</p>}

          <div className="running-total">
            <h3>Current Total: KES {runningTotal.toFixed(2)}</h3>
          </div>

          <button type="submit" className="submit-button">
            Continue to Payment
          </button>
        </form>
      ) : (
        <div className="payment-summary">
          <h3>Payment Summary</h3>
          <ul>
            {Object.entries(formData.selectedCategories)
              .filter(([_, selected]) => selected)
              .map(([category]) => (
                <li key={category}>
                  {formData.categories[category].label} - KES{" "}
                  {formData.categories[category].amount}
                </li>
              ))}
          </ul>

          {formData.showDepartment && (
            <>
              {Object.entries(formData.selectedDepartments).filter(
                ([dept, selected]) => selected
              ).length > 0 && (
                <>
                  <h4>Departments:</h4>
                  <ul className="department-list">
                    {Object.entries(formData.selectedDepartments)
                      .filter(([dept, selected]) => selected)
                      .map(([dept]) => (
                        <li key={dept} className="ml-4">
                          {dept} - KES {formData.departmentAmounts[dept]}
                        </li>
                      ))}
                  </ul>
                </>
              )}
              {formData.showOtherSection &&
                Object.entries(formData.selectedOtherDepartments).filter(
                  ([dept, selected]) => selected
                ).length > 0 && (
                  <>
                    <h4>Other (Promotional):</h4>
                    <ul className="department-list">
                      {Object.entries(formData.selectedOtherDepartments)
                        .filter(([dept, selected]) => selected)
                        .map(([dept]) => (
                          <li key={dept} className="ml-4">
                            {dept} - KES {formData.otherDepartmentAmounts[dept]}
                          </li>
                        ))}
                    </ul>
                  </>
                )}
            </>
          )}

          <h3>Total Amount: KES {runningTotal.toFixed(2)}</h3>
          <p>Phone Number: {formData.phoneNumber}</p>

          {paymentStatus.loading ? (
            <div className="payment-status loading">
              <p>Processing payment...</p>
            </div>
          ) : paymentStatus.error ? (
            <div className="payment-status error">
              <p>{paymentStatus.message}</p>
              <button
                onClick={() =>
                  setPaymentStatus({
                    loading: false,
                    error: null,
                    success: false,
                    message: "",
                  })
                }
              >
                Try Again
              </button>
            </div>
          ) : paymentStatus.success ? (
            <div className="payment-status success">
              <p>{paymentStatus.message}</p>
            </div>
          ) : (
            <div className="payment-actions">
              <button onClick={() => setShowSummary(false)}>
                Modify Payment
              </button>
              <button
                onClick={handlePayment}
                disabled={!formData.phoneNumber || paymentStatus.loading}
              >
                Pay with M-Pesa
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OfferingForm;
